import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Divider,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CommonModal from "../../components/common/CommonModal";
import { AdmitCardFormData, ExamSchedule } from "../../hooks/useAdmitCards";
import { useStudents } from "../../hooks/useStudents";
import { useSubjects, useSubjectsByClass } from "../../hooks/useSubjects";
import { useSessionClass } from "contexts/SessionClassContext";
import { useTranslation } from "react-i18next";
import { ClassData, useClasses } from "../../hooks/useClasses";
import { useSessions } from "../../hooks/useSession";
import { useAuth } from "contexts/AuthContext";
import { useTenantHeader } from "hooks/useTenantHeader";
import ExamTypeOptions from "../enum/ExamTypeOptions";
import DayOptions from "../enum/DayOptions";

interface AdmitCardFormModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: AdmitCardFormData & { id?: number };
  onSubmit: (data: AdmitCardFormData) => void;
  isEdit: boolean;
}

const initialExamSchedule: ExamSchedule = {
  id: 0,
  serialNumber: 1,
  subjectCode: "",
  subjectName: "",
  day: "",
  examDate: "",
  startTime: "",
  endTime: "",
  admitCardId: 0,
  createdAt: "",
  updatedAt: "",
  examName: "",
  // classId: 0, // Removed classId from exam schedule
  subjectId: 0,
  examType: "",
  roomNumber: "",
  instructions: "",
  status: "Scheduled",
  className: "",
};
const statusOptions = ["Scheduled", "Completed", "Cancelled"];

// Section Title Component
const SectionTitle = ({ title }: { title: string }) => (
  <Box sx={{ 
    p: 1, 
    mb: 2, 
    backgroundColor: '#f8f9fb',
    borderLeft: '4px solid #0168EA',
    borderRadius: 1,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  }}>
    <Typography variant="h6" sx={{ 
      color: '#0168EA', 
      fontWeight: 600,
      mb: 0
    }}>
      {title}
    </Typography>
  </Box>
);

function padTime(t: any) {
  // Accepts "14:02" or "14:02:00" and returns "14:02:00"
  if (!t) return "";
  if (/^\d{2}:\d{2}$/.test(t)) return t + ":00";
  return t;
}


// Helper function to convert time from various backend formats to HH:mm string format
function convertTimeFromBackend(timeInput: any): string {
  if (!timeInput) return "";
  
  // Handle array format [hour, minute] from backend
  if (Array.isArray(timeInput) && timeInput.length === 2) {
    const [hour, minute] = timeInput;
    if (typeof hour === 'number' && typeof minute === 'number') {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
  }
  
  // Handle string format "14:00" or "14:00:00"
  if (typeof timeInput === 'string') {
    // If it's already in HH:mm format, return as is
    if (/^\d{2}:\d{2}$/.test(timeInput)) {
      return timeInput;
    }
    // If it's in HH:mm:ss format, extract HH:mm
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeInput)) {
      return timeInput.substring(0, 5);
    }
    // If it's in H:mm format, pad the hour
    if (/^\d{1,2}:\d{2}$/.test(timeInput)) {
      const [hour, minute] = timeInput.split(':');
      return `${hour.padStart(2, '0')}:${minute}`;
    }
  }
  
  return "";
}

// Update the formatDate function to use the same logic as TeacherFormModal
function formatDate(dateString: string): string {
  if (!dateString) return "";

  try {
    // Handle different date formats
    let date: Date;
    
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Try to parse the date
    if (dateString.includes('T')) {
      // ISO string format
      date = new Date(dateString);
    } else {
      // Try parsing as local date to avoid timezone issues
      const [year, month, day] = dateString.split('-').map(Number);
      if (year && month && day) {
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
    }
    
    if (date && date instanceof Date && !isNaN(date.getTime())) {
      // Use local date formatting to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;
      
      return formattedDate;
    }
    return dateString; // Return original if date is invalid
  } catch (error) {
    return dateString; // Return original string if formatting fails
  }
}


const AdmitCardFormModal: React.FC<AdmitCardFormModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
}) => {
  const initialFormState: AdmitCardFormData = {
    examType: "",
    venue: "",
    instructions: "",
    startTime: "",
    endTime: "",
    issued: false,
    examSchedules: [initialExamSchedule],
  };

  const {
    selectedSession,
    setSelectedSession,
    selectedClass,
    selectedClassName,
    setSelectedClass,
  } = useSessionClass();
  const { t } = useTranslation();
  const { studentsQuery } = useStudents();

  const coerceId = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return Number.isNaN(value) ? null : value;
    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  // Filter exam type options based on class
  const boardClass =
    selectedClassName &&
    (selectedClassName.trim().startsWith("10th") ||
      selectedClassName.trim().startsWith("12th"));
  const filteredExamTypeOptions = boardClass
    ? [
        { value: "QUARTERLY", label: "Quarterly" },
        { value: "HALF-YEARLY", label: "Half-Yearly" },
        { value: "PRE-BOARD", label: "Pre-Board" },
        { value: "BOARD", label: "Board" },
      ]
    : [
        { value: "QUARTERLY", label: "Quarterly" },
        { value: "HALF-YEARLY", label: "Half-Yearly" },
        { value: "ANNUAL", label: "Annual" },
      ];

  // Get students for the selected class
  const classStudents = (studentsQuery.data || []).filter(
    (s) =>
      selectedClass &&
      s.schoolClassId === selectedClass.id &&
      s.sessionId === selectedSession?.id
  );

  // State for form data
  const [formData, setFormData] = useState<AdmitCardFormData>(initialFormState);
  const [filteredSubjects, setFilteredSubjects] = useState<any[]>([]);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Keep examSchedules.examName in sync with examType
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      examSchedules: prev.examSchedules.map((sch) => ({
        ...sch,
        examName: prev.examType,
      })),
    }));
  }, [formData.examType]);

  const { user } = useAuth();
  const schoolId = user?.schoolId ?? undefined;
  const { tenantLoading } = useTenantHeader();
  const { sessionsQuery } = useSessions(schoolId);
  const { classesQuery } = useClasses(schoolId);

  const sessionOptions = sessionsQuery.data || [];
  const classOptions = classesQuery.data || [];

  const filteredClassOptions = useMemo(() => {
    if (!classOptions || classOptions.length === 0) return [];
    if (!selectedSession?.id) return classOptions;

    return classOptions.filter((cls: any) => {
      const clsSessionId =
        coerceId(cls.sessionId) ??
        coerceId(cls.session?.id) ??
        coerceId((cls.sessionId as any)?.id);
      if (clsSessionId === null) return true;
      return clsSessionId === coerceId(selectedSession.id);
    });
  }, [classOptions, selectedSession]);

  const hasSessionSelected = coerceId(selectedSession?.id) !== null;
  const hasClassSelected = coerceId(selectedClass?.id) !== null;

  const handleSessionSelectChange = (event: any) => {
    const value = event.target.value;
    if (value === "") {
      setSelectedSession(null);
      setSelectedClass(null);
      return;
    }
    const numericValue = Number(value);
    const sessionObj = sessionOptions.find(
      (session: any) => Number(session.id) === numericValue
    );
    setSelectedSession(sessionObj || null);
    setSelectedClass(null);
  };

  const handleClassSelectChange = (event: any) => {
    const value = event.target.value;
    if (value === "") {
      setSelectedClass(null);
      return;
    }
    const numericValue = Number(value);
    const classObj = filteredClassOptions.find(
      (cls: any) => Number(cls.id) === numericValue
    );
    setSelectedClass(classObj || null);
  };

  const subjectsByClassQuery = useSubjectsByClass(selectedClassName);

  // When modal opens or selectedClass/initialData changes, set form data
  useEffect(() => {
    if (!open) return;

    // If editing, use initialData regardless of whether student is in current classStudents
    if (isEdit && initialData) {
      // Backend now sends boolean directly, no conversion needed
      const formattedData = {
        ...initialFormState,
        ...initialData,
        // Backend now sends boolean directly
        issued: initialData.issued,
        // Ensure time fields are properly initialized - handle both array and string formats
        startTime: convertTimeFromBackend(initialData.startTime),
        endTime: convertTimeFromBackend(initialData.endTime),
        examSchedules: initialData.examSchedules?.map((schedule) => ({
          ...schedule,
          examDate: formatDate(schedule.examDate),
        })) || [initialExamSchedule],
      };

      setFormData(formattedData);
    } else if (classStudents.length > 0) {
      // For add, default to first student
      setFormData({
        ...initialFormState,
      });
    } else {
      // No students available
      setFormData({ ...initialFormState });
    }
    // eslint-disable-next-line
  }, [open, initialData, isEdit, selectedClass, studentsQuery.data]);

  // Update the useEffect that fetches subjects
  useEffect(() => {
    if (selectedClassName) {
      setFilteredSubjects(subjectsByClassQuery.data || []);
    } else {
      setFilteredSubjects([]);
    }
  }, [selectedClassName, subjectsByClassQuery.data]);

  // Add the same date change handler as TeacherFormModal
  const handleDateChange = (name: string) => (date: Date | null) => {
    if (date && date instanceof Date && !isNaN(date.getTime())) {
      try {
        // Use local date formatting to avoid timezone issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const formattedDate = `${year}-${month}-${day}`;

        setFormData((prev) => ({
          ...prev,
          [name]: formattedDate,
        }));
      } catch (error) {
        // Fallback to manual formatting
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const formattedDate = `${year}-${month}-${day}`;

        setFormData((prev) => ({
          ...prev,
          [name]: formattedDate,
        }));
      }
    }
  };

  // Add schedule date change handler
  const handleScheduleDateChange =
    (idx: number, name: string) => (date: Date | null) => {
      if (date && date instanceof Date && !isNaN(date.getTime())) {
        try {
          // Use local date formatting to avoid timezone issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const formattedDate = `${year}-${month}-${day}`;
          
          // Get the day name from the date (0 = Sunday, 1 = Monday, etc.)
          const dayOfWeek = date.getDay();
          const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
          const dayValue = dayNames[dayOfWeek];

          setFormData((prev) => ({
            ...prev,
            examSchedules: prev.examSchedules.map((sch, i) =>
              i === idx 
                ? { ...sch, [name]: formattedDate, day: dayValue } 
                : sch
            ),
          }));
        } catch (error) {
          // Fallback to manual formatting
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const formattedDate = `${year}-${month}-${day}`;
          
          // Get the day name from the date
          const dayOfWeek = date.getDay();
          const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
          const dayValue = dayNames[dayOfWeek];

          setFormData((prev) => ({
            ...prev,
            examSchedules: prev.examSchedules.map((sch, i) =>
              i === idx 
                ? { ...sch, [name]: formattedDate, day: dayValue } 
                : sch
            ),
          }));
        }
      }
    };

  // Add time change handler for HTML time input
  const handleTimeChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    
    if (value) {
      // HTML time input already provides HH:MM format
      const formattedTime = value;
      
      setFormData((prev) => ({
        ...prev,
        [name]: formattedTime,
      }));

      // Clear validation errors when user is typing
      if (formErrors[name]) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    } else {
      // Handle empty case
      setFormData((prev) => ({
        ...prev,
        [name]: "",
      }));

      // Clear validation errors when user is typing
      if (formErrors[name]) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // Update the handleChange function to handle main exam date validation
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));

    // Clear validation errors when user is typing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // SubjectName change: auto-fill subjectCode and subjectId
  const handleSubjectNameChange = (idx: number, e: any) => {
    const subjectName = e.target.value;
    const subject = filteredSubjects.find((s) => s.name === subjectName);
    setFormData((prev) => ({
      ...prev,
      examSchedules: prev.examSchedules.map((sch, i) =>
        i === idx
          ? {
              ...sch,
              subjectName,
              subjectCode: subject?.subjectCode || "",
              subjectId: subject?.id || 0,
              // classId: subject?.className || "", // Removed classId assignment
              className: subject?.className || "",
            }
          : sch
      ),
    }));
  };

  const handleAddSchedule = () => {
    setFormData((prev) => ({
      ...prev,
      examSchedules: [
        ...prev.examSchedules,
        { ...initialExamSchedule, examName: formData.examType },
      ],
    }));
  };

  const handleRemoveSchedule = (idx: number) => {
    setFormData((prev) => ({
      ...prev,
      examSchedules: prev.examSchedules.filter((_, i) => i !== idx),
    }));
  };


  const handleClassChange = (e: any) => {
    const className = e.target.value;
    setSelectedClass({ name: className } as ClassData);
    setFormData((prev) => ({
      ...prev,
      studentId: 0,
    }));
  };

  // Update the handleScheduleChange function to remove date validation
  const handleScheduleChange = (
    idx: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      examSchedules: prev.examSchedules.map((sch, i) =>
        i === idx ? { ...sch, [name]: value } : sch
      ),
    }));

    // Clear validation errors when user is typing
    if (formErrors[`schedule_${idx}_${name}`]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`schedule_${idx}_${name}`];
        return newErrors;
      });
    }
  };

  // Update the validateForm function to include time validation
  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    // Validate startTime and endTime
    if (!formData.startTime) {
      errors.startTime = t("start_time_required");
    } else if (!/^\d{2}:\d{2}$/.test(formData.startTime)) {
      errors.startTime = t("invalid_time_format");
    }
    
    if (!formData.endTime) {
      errors.endTime = t("end_time_required");
    } else if (!/^\d{2}:\d{2}$/.test(formData.endTime)) {
      errors.endTime = t("invalid_time_format");
    }

    // Validate that endTime is after startTime
    if (formData.startTime && formData.endTime && 
        /^\d{2}:\d{2}$/.test(formData.startTime) && 
        /^\d{2}:\d{2}$/.test(formData.endTime)) {
      const startTimeMinutes = timeToMinutes(formData.startTime);
      const endTimeMinutes = timeToMinutes(formData.endTime);
      
      if (endTimeMinutes <= startTimeMinutes) {
        errors.endTime = t("end_time_must_be_after_start_time");
      }
    }

    // Validate exam schedules
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    formData.examSchedules.forEach((schedule, index) => {
      if (!schedule.examDate) {
        errors[`schedule_${index}_examDate`] = t("schedule_exam_date_required");
      } else {
        const examDate = new Date(schedule.examDate);
        if (!isNaN(examDate.getTime())) {
          examDate.setHours(0, 0, 0, 0);
          if (examDate < today) {
            errors[`schedule_${index}_examDate`] = t("schedule_exam_date_cannot_be_past");
          }
        }
      }
    });

    return errors;
  };

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form first
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    try {
      // Prepare the data to submit
      const submitData = {
        ...formData,
        classId: selectedClass?.id,
        examSchedules: formData.examSchedules,
        sessionId: selectedSession?.id,
      };

      // Validate that all required fields are present
      if (!submitData.classId) {
        throw new Error("Class ID is required");
      }
      if (!submitData.sessionId) {
        throw new Error("Session ID is required");
      }
      if (!submitData.startTime || !submitData.endTime) {
        throw new Error("Start time and end time are required");
      }

      // Validate time format (HH:mm) - will be converted to HH:mm:ss for backend
      const timeFormatRegex = /^\d{2}:\d{2}$/;
      if (!timeFormatRegex.test(submitData.startTime)) {
        throw new Error(`Invalid start time format: ${submitData.startTime}. Expected HH:mm format.`);
      }
      if (!timeFormatRegex.test(submitData.endTime)) {
        throw new Error(`Invalid end time format: ${submitData.endTime}. Expected HH:mm format.`);
      }

      // Validate exam schedules have required fields
      if (submitData.examSchedules && submitData.examSchedules.length > 0) {
        submitData.examSchedules.forEach((schedule, index) => {
          // Only validate subjectId if it's a new schedule (id === 0) or if it's explicitly set
          if (schedule.id === 0 && (!schedule.subjectId || schedule.subjectId === 0)) {
            throw new Error(`Subject ID is required for exam schedule ${index + 1}`);
          }
          if (!schedule.examDate) {
            throw new Error(`Exam date is required for exam schedule ${index + 1}`);
          }
          if (!schedule.subjectName) {
            throw new Error(`Subject name is required for exam schedule ${index + 1}`);
          }
          if (!schedule.day) {
            throw new Error(`Day is required for exam schedule ${index + 1}`);
          }
        });
      }

      // Clean up exam schedules data to ensure it matches backend expectations
      const cleanedExamSchedules = submitData.examSchedules?.map(schedule => ({
        ...schedule,
        // Ensure numeric fields are properly formatted, but preserve existing values for editing
        subjectId: schedule.subjectId ? Number(schedule.subjectId) : (schedule.id ? schedule.subjectId : 0),
        serialNumber: Number(schedule.serialNumber) || 1,
        // Remove any undefined or null values that might cause issues
        examName: schedule.examName || submitData.examType,
        status: schedule.status || "Scheduled"
      })) || [];

      const finalSubmitData = {
        ...submitData,
        // Use padTime to ensure HH:mm:ss format for backend LocalTime
        startTime: padTime(submitData.startTime),
        endTime: padTime(submitData.endTime),
        examSchedules: cleanedExamSchedules
      };

      await onSubmit(finalSubmitData);
    } catch (error: any) {
      
      let message = t("failed_submit_admit_card");

      if (error?.response?.status === 500) {
        // Check for specific transaction error
        if (error?.response?.data?.message?.includes("current transaction is aborted")) {
          message = "Database transaction error. Please try again in a moment. If the problem persists, contact support.";
        } else if (error?.response?.data?.message?.includes("audit_entry")) {
          message = "System logging error. Please try again. If the problem persists, contact support.";
        } else {
          message = t("server_error_please_try_again");
        }
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }

      setErrorDialogMessage(message);
      setErrorDialogOpen(true);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <CommonModal
        open={open}
        onClose={handleClose}
        title={isEdit ? t("edit_admit_card") : t("add_admit_card")}
        maxWidth="lg"
        disableEscapeKeyDown
        hideActions
        contentSx={{ p: 0 }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ p: 2 }}>
            <Paper sx={{ p: 2 }}>
              {/* Section 1: Basic Exam Information */}
              <SectionTitle title={t("basic_exam_information")} />
              <Grid container spacing={1.7}>
                {/* First Row - 2 fields */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required size="small" margin="dense">
                    <InputLabel>{t("exam_type")}</InputLabel>
                    <Select
                      label={t("exam_type")}
                      name="examType"
                      value={formData.examType}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          examType: value,
                          examSchedules: prev.examSchedules.map((sch) => ({
                            ...sch,
                            examName: value,
                          })),
                        }));
                      }}
                      required
                      size="small"
                      margin="dense"
                      variant="outlined"
                      disabled={isEdit}
                      sx={{ 
                        backgroundColor: '#f8f9fb',
                        '& .MuiOutlinedInput-root': {
                          height: '40px'
                        }
                      }}
                    >
                      {filteredExamTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("venue")}
                    name="venue"
                    value={formData.venue}
                    onChange={handleChange}
                    required
                    margin="dense"
                    size="small"
                    variant="outlined"
                    sx={{
                      backgroundColor: '#f8f9fb',
                      '& .MuiOutlinedInput-root': {
                        height: '40px'
                      }
                    }}
                  />
                </Grid>

                {/* Second Row - 2 fields */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("start_time")}
                    name="startTime"
                    type="time"
                    value={formData.startTime}
                    onChange={handleTimeChange("startTime")}
                    margin="dense"
                    size="small"
                    variant="outlined"
                    error={!!formErrors.startTime}
                    helperText={formErrors.startTime}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      backgroundColor: '#f8f9fb',
                      '& .MuiOutlinedInput-root': {
                        height: '40px'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("end_time")}
                    name="endTime"
                    type="time"
                    value={formData.endTime}
                    onChange={handleTimeChange("endTime")}
                    margin="dense"
                    size="small"
                    variant="outlined"
                    error={!!formErrors.endTime}
                    helperText={formErrors.endTime}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      backgroundColor: '#f8f9fb',
                      '& .MuiOutlinedInput-root': {
                        height: '40px'
                      }
                    }}
                  />
                </Grid>

                {/* Third Row - Instructions (full width) */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("instructions")}
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleChange}
                    margin="dense"
                    size="small"
                    variant="outlined"
                    multiline
                    rows={3}
                    sx={{
                      backgroundColor: '#f8f9fb',
                      '& .MuiOutlinedInput-root': {
                        alignItems: 'flex-start'
                      },
                      '& .MuiInputBase-inputMultiline': {
                        overflow: 'auto !important',
                        resize: 'none'
                      }
                    }}
                  />
                </Grid>

                {/* Fourth Row - Issued field */}
                <Grid item xs={12} md={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1,
                    backgroundColor: formData.issued ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: 1,
                    border: `1px solid ${formData.issued ? '#1976d2' : '#e0e0e0'}`,
                    transition: 'all 0.2s ease'
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.issued}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              issued: e.target.checked,
                            }))
                          }
                          name="issued"
                          color="primary"
                          sx={{
                            '&.Mui-checked': {
                              color: '#1976d2',
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: formData.issued ? '#1976d2' : '#666',
                            }}
                          >
                            {t("issued")}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: formData.issued ? '#1976d2' : '#999',
                              fontStyle: 'italic'
                            }}
                          >
                            {formData.issued ? '(Yes)' : '(No)'}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        margin: 0,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '14px',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4, borderColor: '#e0e7ff', borderWidth: 1 }} />

              {/* Section 2: Exam Schedules */}
              <SectionTitle title={t("exam_schedules")} />
              {formData.examSchedules.map((schedule, idx) => (
                <Box
                  key={idx}
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Grid container spacing={2}>
                    {/* Row 1: Subject Name, Subject Code, Day, Exam Date */}
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth required size="small" margin="dense">
                        <InputLabel>{t("subject_name")}</InputLabel>
                        <Select
                          label={t("subject_name")}
                          name="subjectName"
                          value={schedule.subjectName}
                          onChange={(e) => handleSubjectNameChange(idx, e)}
                          required
                          size="small"
                          margin="dense"
                          variant="outlined"
                          MenuProps={{
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left"
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left"
                            },
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                marginTop: 4
                              }
                            }
                          }}
                          sx={{ 
                            backgroundColor: '#f8f9fb',
                            '& .MuiSelect-select': {
                              minHeight: '29px',   // Increase height properly
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        >
                          {(() => {
                            // Get all selected subject names from other schedules (excluding current index)
                            const selectedSubjectNames = formData.examSchedules
                              .map((sch, i) => i !== idx ? sch.subjectName : null)
                              .filter((name): name is string => name !== null && name !== "");
                            
                            // Filter subjects to exclude already selected ones, but include current schedule's selected subject
                            const availableSubjects = (subjectsByClassQuery.data || []).filter(
                              (subject) => 
                                !selectedSubjectNames.includes(subject.name) || 
                                subject.name === schedule.subjectName
                            );
                            
                            return availableSubjects.map((subject) => (
                              <MenuItem key={subject.id} value={subject.name}>
                                {subject.name}
                              </MenuItem>
                            ));
                          })()}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t("subject_code")}
                        name="subjectCode"
                        value={schedule.subjectCode}
                        margin="dense"
                        InputProps={{ readOnly: true }}
                        size="small"
                        variant="outlined"
                        sx={{ backgroundColor: '#f8f9fb' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DatePicker
                        label={
                          <span>
                            {t("exam_date")}{" "}
                            <span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        value={
                          schedule.examDate
                            ? (() => {
                                const date = new Date(schedule.examDate);
                                return isNaN(date.getTime()) ? null : date;
                              })()
                            : null
                        }
                        onChange={handleScheduleDateChange(idx, "examDate")}
                        minDate={new Date()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            margin: "dense",
                            size: "small",
                            variant: "outlined",
                            error: !!formErrors[`schedule_${idx}_examDate`],
                            helperText: formErrors[`schedule_${idx}_examDate`],
                            sx: { backgroundColor: '#f8f9fb' }
                          },
                        }}
                        format="dd/MM/yyyy"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth required size="small" margin="dense">
                        <InputLabel>{t("day")}</InputLabel>
                        <Select
                          label={t("day")}
                          name="day"
                          value={schedule.day}
                          onChange={(e) => {
                            const { name, value } = e.target;
                            handleScheduleChange(idx, {
                              target: { name, value },
                            } as any);
                          }}
                          required
                          size="small"
                          margin="dense"
                          variant="outlined"
                          disabled
                          MenuProps={{
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left"
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left"
                            },
                          }}
                          sx={{ 
                            backgroundColor: '#f8f9fb',
                            '& .MuiSelect-select': {
                              minHeight: '30px',   // Increase height properly
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        >
                          {DayOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    {formData.examSchedules.length > 1 && (
                      <Button
                        color="error"
                        onClick={() => handleRemoveSchedule(idx)}
                      >
                        {t("remove")}
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
              <Button 
                variant="outlined" 
                onClick={handleAddSchedule}
                sx={{ 
                  borderColor: '#0168EA',
                  color: '#0168EA',
                  '&:hover': {
                    borderColor: '#0056d3',
                    backgroundColor: 'rgba(1, 104, 234, 0.04)'
                  }
                }}
              >
                {t("add_exam_schedule")}
              </Button>
            </Paper>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 1.5, backgroundColor: '#f8f9fb', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              size="large"
              sx={{
                minWidth: 120,
                borderColor: "#3092e3",
                color: "#3092e3",
                "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" }
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                minWidth: 120,
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" }
              }}
            >
              {isEdit ? t("update") : t("create")}
            </Button>
          </Box>
        </form>
      </CommonModal>
      {/* Error Popup Dialog */}
      <CommonModal
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        title={t("error")}
        maxWidth="xs"
        customActions={
          <Button
            onClick={() => setErrorDialogOpen(false)}
            variant="contained"
            autoFocus
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {t("ok")}
          </Button>
        }
      >
        <Typography color="error">{errorDialogMessage}</Typography>
      </CommonModal>
    </LocalizationProvider>
  );
};

export default AdmitCardFormModal;


